import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet'
import { sizes, colours } from '@qflow/theme'
import Draggable from 'react-draggable'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import * as AntIcons from '@ant-design/icons'
import {
  Modal as AntModal,
  Button,
  Col,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Spin,
  Tooltip,
  Input
} from 'antd'
import { icons, Text, Spacer, ColourButton } from 'portal/lib/primitives'
import { useModuleIds } from 'portal/lib/hooks'
import {
  sugstAddrsSearch,
  addrsSearchStart,
  bulkSugstAddrsSearch
} from 'portal/store/address/actions'
import {
  KnownAddressResult,
  ClientAddressName
} from './ClientKnownAddressModalLink'

import './leaflet.css'

import L from 'leaflet'

const blueIcon = L.icon({
  iconUrl: '../../../assets/marker-icon.png',
  iconRetinaUrl: '../../../assets/marker-icon.png',
  shadowUrl: '../../../assets/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12.5, 41] // bottom center tip
})

const navyIcon = L.icon({
  iconUrl: '../../../assets/navy-marker-icon.png',
  iconRetinaUrl: '../../../assets/navy-marker-icon.png',
  shadowUrl: '../../../assets/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12.5, 41] // bottom center tip
})

type SuggestedAddress = {
  fullAddress: string
  coordinates: {
    lat: number
    lon: number
  }
  postCode: string | null
  country: string | null
  count: number | null
  score: number | null
}

export type ClientKnownAddressModalProps = {
  addressName: ClientAddressName
  isOpen: boolean
  onCancel: () => void
  onSubmit: (result: KnownAddressResult) => void
  otherAddressCoordinates:
    | { lat: number; lon: number }
    | Array<{ lat: number; lon: number }>
    | null
  currentAddressCoordinates: { lat: number; lon: number } | null
  otherAddressName: ClientAddressName
  supplierName?: string | undefined
  tradeContractorName?: string | undefined
}

export function ClientKnownAddressModal({
  addressName,
  isOpen,
  onCancel,
  onSubmit,
  otherAddressCoordinates,
  currentAddressCoordinates,
  otherAddressName,
  supplierName,
  tradeContractorName
}: ClientKnownAddressModalProps) {
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const draggleRef = useRef<HTMLDivElement>(null)

  const onStart = (event: DraggableEvent, uiData: DraggableData) => {
    // Stop event propagation to prevent affecting background components
    if (event instanceof Event) {
      event.stopPropagation()
    }

    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  const onDrag = (event: DraggableEvent) => {
    // Stop event propagation during drag
    if (event instanceof Event) {
      event.stopPropagation()
    }
  }

  const onStop = (event: DraggableEvent) => {
    // Stop event propagation when drag ends
    if (event instanceof Event) {
      event.stopPropagation()
    }
  }
  const { moduleName } = useModuleIds()
  const modalTitle = useMemo(() => `Edit ${addressName} Address`, [addressName])

  const renderPanel = useCallback(() => {
    return (
      <SuggestionAddressPanel
        addressName={addressName}
        onCancel={onCancel}
        onSubmit={onSubmit}
        otherAddressCoordinates={otherAddressCoordinates}
        currentAddressCoordinates={currentAddressCoordinates}
        otherAddressName={otherAddressName}
        supplierName={supplierName}
        tradeContractorName={tradeContractorName}
      />
    )
  }, [
    addressName,
    onCancel,
    onSubmit,
    otherAddressCoordinates,
    currentAddressCoordinates,
    otherAddressName,
    supplierName,
    tradeContractorName
  ])

  return (
    <AntModal
      title={
        <div
          id="drag-handle"
          style={{
            width: '100%',
            cursor: 'move'
          }}
        >
          <Row style={{ alignItems: 'center', display: 'flex' }}>
            <Text dim>{modalTitle}</Text>
            {moduleName === 'delivery' && (
              <>
                <Spacer.Small />
                <img
                  src="../../../assets/marker-icon-small.png"
                  alt="Marker Icon"
                />
              </>
            )}
          </Row>
        </div>
      }
      open={isOpen}
      closeIcon={<icons.Cross colour={colours.BLACK} size={sizes.SMALL} />}
      onCancel={onCancel}
      maskClosable={false}
      footer={null}
      width={moduleName === 'delivery' ? '80vw' : 600}
      style={{
        top: 20,
        maxWidth: '1200px',
        paddingBottom: 0
      }}
      wrapClassName="address-modal"
      modalRender={modal => (
        <Draggable
          handle="#drag-handle"
          bounds={bounds}
          onStart={onStart}
          onDrag={onDrag}
          onStop={onStop}
          enableUserSelectHack={false}
          cancel=".leaflet-container, .ant-input, .ant-radio-group, .ant-btn"
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
      transitionName=""
    >
      {renderPanel()}
    </AntModal>
  )
}

//Necessary otherwise map renders with wrong size
function ResizeMap() {
  const map = useMap()

  useEffect(() => {
    if (map && map.getContainer()) {
      map.invalidateSize()
    }
  }, [map])

  return null
}

function RecenterMap({
  lat,
  lng,
  otherCoordinates
}: {
  lat: number
  lng: number
  otherCoordinates?:
    | { lat: number; lon: number }
    | Array<{ lat: number; lon: number }>
    | null
}) {
  const map = useMap()

  // Calculate center and zoom based on all points
  const { midLat, midLng, zoomLevel } = useMemo(() => {
    const allPoints = [{ lat, lng }]
    if (otherCoordinates) {
      const coordsArray = Array.isArray(otherCoordinates)
        ? otherCoordinates
        : [otherCoordinates]
      if (coordsArray.length > 0) {
        allPoints.push(...coordsArray.map(c => ({ lat: c.lat, lng: c.lon })))
      }
    }

    if (allPoints.length === 1) {
      return { midLat: lat, midLng: lng, zoomLevel: 14 }
    }

    // Calculate bounds
    const latitudes = allPoints.map(p => p.lat)
    const longitudes = allPoints.map(p => p.lng)
    const minLat = Math.min(...latitudes)
    const maxLat = Math.max(...latitudes)
    const minLng = Math.min(...longitudes)
    const maxLng = Math.max(...longitudes)

    const midLat = (minLat + maxLat) / 2
    const midLng = (minLng + maxLng) / 2

    // Calculate distance between furthest points
    const maxDistance = L.latLng(minLat, minLng).distanceTo(
      L.latLng(maxLat, maxLng)
    )

    // Calculate appropriate zoom level
    const zoomLevel =
      maxDistance > 0 ? Math.floor(16 - Math.log2(maxDistance / 900)) : 14

    return { midLat, midLng, zoomLevel: Math.max(2, Math.min(zoomLevel, 18)) }
  }, [lat, lng, otherCoordinates])

  useEffect(() => {
    map.setView([midLat, midLng], zoomLevel)
  }, [midLat, midLng, map, zoomLevel])

  return null
}

function SuggestionAddressPanel({
  addressName,
  onCancel,
  onSubmit,
  otherAddressCoordinates,
  currentAddressCoordinates,
  otherAddressName,
  supplierName,
  tradeContractorName
}: {
  addressName: ClientAddressName
  onCancel: () => void
  onSubmit: (result: KnownAddressResult) => void
  otherAddressCoordinates:
    | { lat: number; lon: number }
    | Array<{ lat: number; lon: number }>
    | null
  currentAddressCoordinates: { lat: number; lon: number } | null
  otherAddressName: ClientAddressName
  supplierName?: string
  tradeContractorName?: string
}) {
  const [enteredValue, setEnteredValue] = useState<string>('')
  const [selectedAddress, setSelectedAddress] =
    useState<SuggestedAddress | null>()
  const { accountId, projectId, moduleName, recordId } = useModuleIds()

  // Normalize otherAddressCoordinates to always be an array for easier handling
  const otherCoordinatesArray = useMemo(() => {
    if (!otherAddressCoordinates) return null
    const coordsArray = Array.isArray(otherAddressCoordinates)
      ? otherAddressCoordinates
      : [otherAddressCoordinates]
    // Filter out any invalid coordinates
    const validCoords = coordsArray.filter(
      coord =>
        coord &&
        typeof coord.lat === 'number' &&
        typeof coord.lon === 'number' &&
        !isNaN(coord.lat) &&
        !isNaN(coord.lon)
    )
    return validCoords.length > 0 ? validCoords : null
  }, [otherAddressCoordinates])

  const dispatch = useDispatch()

  function toAddressType(value: ClientAddressName) {
    switch (value) {
      case 'Delivery':
      case 'Site':
        return 'deliveryAddress'
      case 'Dispatch':
        return 'dispatchAddress'
      case 'Origin Site':
        return 'originSiteAddress'
      case 'Facility Site':
        return 'facilitySiteAddress'
    }
  }

  const {
    isLoadingSuggestAddresses,
    suggestAddressesError,
    suggestionsGeneral,
    isLoadingAddressSearch,
    addressSearchError,
    foundAddresses,
    addressesHasSearched,
    siteAddress
  } = useSelector((state: any) => state.address)

  const isLoading = isLoadingSuggestAddresses || isLoadingAddressSearch

  useEffect(() => {
    const addressType = toAddressType(addressName)

    // Use bulk API when supplier and trade contractor are provided (bulk update mode)
    if (supplierName && tradeContractorName) {
      dispatch(
        bulkSugstAddrsSearch(
          accountId,
          projectId,
          moduleName,
          addressType,
          supplierName,
          tradeContractorName
        )
      )
    } else if (recordId) {
      // Use regular API for single record edit
      dispatch(
        sugstAddrsSearch(
          accountId,
          projectId,
          moduleName,
          recordId,
          addressType
        )
      )
    }
  }, [
    accountId,
    addressName,
    dispatch,
    moduleName,
    projectId,
    recordId,
    supplierName,
    tradeContractorName
  ])

  function handleNewAddressSearching() {
    if (enteredValue === '') {
      return
    }
    dispatch(addrsSearchStart(accountId, projectId, enteredValue))
  }

  const handleSuggestedResultSelected = useCallback(
    (e: RadioChangeEvent) => {
      const [selectedType, index] = e.target.value.split('|')
      const selectedAddress =
        selectedType === 'SiteAddress' ? siteAddress : suggestionsGeneral[index]
      setSelectedAddress(selectedAddress)
    },
    [siteAddress, suggestionsGeneral]
  )

  const handleMatchedResultSelected = useCallback(
    (e: RadioChangeEvent) => {
      const selectedMatchedAddress = foundAddresses[e.target.value]
      setSelectedAddress(selectedMatchedAddress)
    },
    [foundAddresses]
  )

  const handleSubmit = useCallback(() => {
    if (!selectedAddress) {
      console.warn('Submit unexpectedly had no selected address')
      return
    }
    onSubmit({
      entered: enteredValue ?? '',
      known: selectedAddress.fullAddress,
      coordinates: selectedAddress.coordinates
    })
  }, [enteredValue, onSubmit, selectedAddress])

  const renderSuggestedAddresses = useCallback(
    (resultType: 'SiteAddress' | 'General', addresses: SuggestedAddress[]) => (
      <Space direction="vertical">
        {addresses.map((a, i) => (
          <Radio key={i} value={`${resultType}|${i}`}>
            <span className="emphasise">{a.fullAddress}</span>{' '}
            {a.count && (
              <>
                <br />({a.count} records)
              </>
            )}
          </Radio>
        ))}
      </Space>
    ),
    []
  )

  const SearchButton = (
    <Button
      tabIndex={-1}
      icon={<AntIcons.SearchOutlined />}
      onClick={() => handleNewAddressSearching()}
      aria-label="Search"
      disabled={!enteredValue}
      style={{
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
        borderColor: colours.BORDER_GREY,
        backgroundColor: enteredValue ? colours.TEAL : colours.CLOUD_GREY,
        color: colours.WHITE
      }}
    />
  )

  return (
    <Row
      style={{
        height: 'min(650px, calc(90vh - 100px))'
      }}
    >
      <Col
        style={{
          width: moduleName === 'delivery' ? 400 : 600,
          height: 'min(650px, calc(90vh - 100px))',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '8px',
            marginBottom: '16px'
          }}
        >
          {(suggestionsGeneral.length !== 0 || siteAddress !== null) && (
            <h2>Select an existing address</h2>
          )}
          {suggestionsGeneral.length === 0 && siteAddress === null && (
            <h2>Search street address</h2>
          )}
          {isLoading && (
            <Row>
              <Col offset={10} span={4}>
                <Spin tip="Finding Suggestions..." />
              </Col>
            </Row>
          )}

          {!isLoading && suggestAddressesError && (
            <p className="error">
              Failed to retrieve suggested addresses:{' '}
              {typeof suggestAddressesError === 'string'
                ? suggestAddressesError
                : suggestAddressesError?.message || 'Unknown error'}
            </p>
          )}

          {!isLoading &&
            !suggestAddressesError &&
            (suggestionsGeneral.length !== 0 || siteAddress !== null) && (
              <Radio.Group onChange={handleSuggestedResultSelected}>
                {siteAddress && (
                  <Col>
                    <Row>
                      <Text size={sizes.SMALL_MEDIUM} dim>
                        Default project site address
                      </Text>
                    </Row>
                    <Spacer.Fine />
                    <Row>
                      {renderSuggestedAddresses('SiteAddress', [siteAddress])}
                    </Row>
                  </Col>
                )}
                {suggestionsGeneral.length > 0 && (
                  <Col>
                    <Spacer.Fine />
                    <Row>
                      <Text size={sizes.SMALL_MEDIUM} dim>
                        {addressName} addresses on other records
                      </Text>
                    </Row>
                    <Spacer.Fine />
                    <Row>
                      {renderSuggestedAddresses('General', suggestionsGeneral)}
                    </Row>
                  </Col>
                )}
              </Radio.Group>
            )}
          {(suggestionsGeneral.length !== 0 || siteAddress !== null) && (
            <>
              <Spacer.Large />
              <h2>or, search street address</h2>
            </>
          )}

          <Row style={{ marginBottom: '10px' }}>
            <Col flex="auto">
              <Input.Search
                id="addressSearchInput"
                defaultValue={''}
                onChange={e => setEnteredValue(e.target.value)}
                onPressEnter={() => handleNewAddressSearching()}
                enterButton={SearchButton}
              />
            </Col>
          </Row>

          {isLoading && (
            <Row>
              <Col offset={10} span={4}>
                <Spin tip="Searching..." />
              </Col>
            </Row>
          )}

          {!isLoading && addressSearchError && (
            <p className="error">
              Failed to search addresses:{' '}
              {typeof addressSearchError === 'string'
                ? addressSearchError
                : addressSearchError?.message || 'Unknown error'}
            </p>
          )}

          {!isLoading &&
            !addressSearchError &&
            addressesHasSearched &&
            foundAddresses.length === 0 && (
              <p className="warning">No addresses found matching search text</p>
            )}

          {!isLoading && !addressSearchError && foundAddresses.length > 0 && (
            <Radio.Group onChange={handleMatchedResultSelected}>
              <Space direction="vertical">
                {foundAddresses.map((a, i) => (
                  <Radio key={i} value={i}>
                    <Tooltip title={`${a.country}`}>
                      <span className="emphasise">{a.fullAddress}</span>
                    </Tooltip>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          )}
        </div>

        <Row
          style={{
            justifyContent: 'flex-end',
            flexShrink: 0,
            paddingTop: '8px',
            borderTop: `1px solid ${colours.BORDER_GREY}`
          }}
        >
          <Col>
            <ColourButton.Basic onClick={onCancel}>Cancel</ColourButton.Basic>
          </Col>
          <Spacer.Tiny />
          <Col>
            <ColourButton.Action
              onClick={handleSubmit}
              disabled={!selectedAddress}
            >
              Set and close
            </ColourButton.Action>
          </Col>
        </Row>
      </Col>
      <Col flex="auto" style={{ marginLeft: '1rem' }}>
        {moduleName === 'delivery' && (
          <MapContainer
            dragging={true}
            center={
              currentAddressCoordinates
                ? [currentAddressCoordinates.lat, currentAddressCoordinates.lon]
                : otherCoordinatesArray && otherCoordinatesArray.length > 0
                  ? [otherCoordinatesArray[0].lat, otherCoordinatesArray[0].lon]
                  : [12, -12]
            }
            zoom={
              currentAddressCoordinates ||
              (otherCoordinatesArray && otherCoordinatesArray.length > 0)
                ? 12
                : 2
            }
            scrollWheelZoom={true}
          >
            <ResizeMap />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {!selectedAddress && currentAddressCoordinates && (
              <>
                <Marker
                  icon={blueIcon}
                  position={[
                    currentAddressCoordinates.lat,
                    currentAddressCoordinates.lon
                  ]}
                  title={`${addressName} Address`}
                ></Marker>

                <RecenterMap
                  lat={currentAddressCoordinates.lat}
                  lng={currentAddressCoordinates.lon}
                  otherCoordinates={otherCoordinatesArray}
                />
              </>
            )}
            {selectedAddress && (
              <>
                <Marker
                  key={`${addressName}-selected`}
                  icon={blueIcon}
                  position={[
                    selectedAddress.coordinates.lat,
                    selectedAddress.coordinates.lon
                  ]}
                  title={`${addressName} Address`}
                ></Marker>

                <RecenterMap
                  lat={selectedAddress.coordinates.lat}
                  lng={selectedAddress.coordinates.lon}
                  otherCoordinates={otherCoordinatesArray}
                />
              </>
            )}
            {/* Show all delivery addresses when there's no dispatch address yet */}
            {!selectedAddress &&
              !currentAddressCoordinates &&
              otherCoordinatesArray &&
              otherCoordinatesArray.length > 0 && (
                <RecenterMap
                  lat={otherCoordinatesArray[0].lat}
                  lng={otherCoordinatesArray[0].lon}
                  otherCoordinates={
                    otherCoordinatesArray.length > 1
                      ? otherCoordinatesArray.slice(1)
                      : null
                  }
                />
              )}
            {otherCoordinatesArray &&
              otherCoordinatesArray.map((coords, index) => {
                if (
                  !coords ||
                  typeof coords.lat !== 'number' ||
                  typeof coords.lon !== 'number'
                ) {
                  return null
                }
                return (
                  <Marker
                    key={`${otherAddressName}-${index}`}
                    icon={navyIcon}
                    position={[coords.lat, coords.lon]}
                    title={
                      otherCoordinatesArray.length > 1
                        ? `${otherAddressName} Address ${index + 1}`
                        : `${otherAddressName} Address`
                    }
                  ></Marker>
                )
              })}
          </MapContainer>
        )}
      </Col>
    </Row>
  )
}
